import type { LeaderboardEntry, LeaderboardPlayerSummary, PlayerSearchResult, RivalsResponse } from '@/types'
import { apiService } from '@/services/api'

type LeaderboardTab = 'global' | 'rivals' | 'search'

export class LeaderboardManager {
  private currentTab: LeaderboardTab = 'global'
  private userId: string
  private searchQuery = ''
  private searchRequestId = 0
  private readonly playerArrowIcon = '<svg viewBox="0 0 24 24" role="presentation" focusable="false" aria-hidden="true"><path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>'

  constructor(userId: string) {
    this.userId = userId
    this.setupTabNavigation()
    this.setupSearchForm()
    this.setupProfileNavigation()
  }

  private setupTabNavigation(): void {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      const tabBtn = target.closest('.tab-btn') as HTMLButtonElement
      if (!tabBtn) return

      const tab = tabBtn.dataset.tab
      if (tab === 'global' || tab === 'rivals' || tab === 'search') {
        this.switchTab(tab)
      }
    })
  }

  private setupSearchForm(): void {
    const form = document.getElementById('leaderboardSearchForm') as HTMLFormElement | null
    if (!form) return

    const input = document.getElementById('leaderboardSearchInput') as HTMLInputElement | null

    form.addEventListener('submit', (event) => {
      event.preventDefault()
      this.performSearch(input?.value ?? '')
    })

    input?.addEventListener('input', () => {
      if (input.value.trim() === '') {
        this.searchQuery = ''
        this.renderSearchPlaceholder()
      }
    })

  }

  private setupProfileNavigation(): void {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const item = target.closest<HTMLLIElement>('.leaderboard-list li[data-player-id]')
      if (!item) return

      const playerId = item.dataset.playerId
      if (!playerId) return

      const detail = {
        id: playerId,
        telegramId: item.dataset.playerTelegramId || null,
        display: item.dataset.playerDisplay || '',
        username: item.dataset.playerUsername || '',
        rank: item.dataset.playerRank ? Number(item.dataset.playerRank) : null,
        totalCracked: item.dataset.playerScore ? Number(item.dataset.playerScore) : undefined,
      }

      window.dispatchEvent(new CustomEvent('openPlayerProfile', { detail }))
    })
  }

  private focusSearchInput(): void {
    const input = document.getElementById('leaderboardSearchInput') as HTMLInputElement | null
    if (!input) return
    input.focus()
    const length = input.value.length
    input.setSelectionRange(length, length)
  }

  private async loadSearchResults(): Promise<void> {
    if (!this.searchQuery) {
      this.renderSearchPlaceholder()
      return
    }

    await this.performSearch(this.searchQuery, { showLoading: false })
  }

  private async performSearch(query: string, options: { showLoading?: boolean } = {}): Promise<void> {
    const listEl = document.getElementById('searchResultsList')
    if (!listEl) return

    const trimmed = query.trim()
    this.searchQuery = trimmed

    if (!trimmed) {
      this.renderSearchPlaceholder()
      return
    }

    if (options.showLoading !== false) {
      listEl.innerHTML = '<li>Searching...</li>'
    }

    const token = ++this.searchRequestId

    try {
      const results = await apiService.searchPlayers(trimmed)
      if (token !== this.searchRequestId) return
      this.renderSearchResults(results, trimmed)
    } catch (error) {
      if (token !== this.searchRequestId) return
      listEl.innerHTML = `<li>Error: ${(error as Error).message}</li>`
    }
  }

  private renderSearchPlaceholder(): void {
    this.renderSearchMessage('Waiting for your search…')
  }

  private renderSearchMessage(message: string): void {
    const listEl = document.getElementById('searchResultsList')
    if (!listEl) return
    listEl.innerHTML = `<li>${this.escapeHtml(message)}</li>`
  }

  private renderSearchResults(results: PlayerSearchResult[], query: string): void {
    const listEl = document.getElementById('searchResultsList')
    if (!listEl) return

    if (results.length === 0) {
      this.renderSearchMessage(`No players found for "${query}"`)
      return
    }

    const html = results
      .map(result => {
        const isYou = result.id === this.userId
        const rankLabel = result.rank != null ? `#${result.rank}` : '#-'
        const telegramId = result.telegram_id ?? result.id
        const displayName = result.display
          ? this.shortenPlayerLabel(result.display)
          : this.formatPlayerId(telegramId)
        const displaySource = result.display || result.username || telegramId

        return `
          <li${isYou ? ' class="you"' : ''}
            data-player-id="${this.escapeAttribute(result.id)}"
            data-player-telegram-id="${this.escapeAttribute(telegramId)}"
            data-player-display="${this.escapeAttribute(displaySource)}"
            data-player-username="${this.escapeAttribute(result.username ?? '')}"
            data-player-rank="${result.rank ?? ''}"
            data-player-score="${result.total_cracked}"
          >
            <div class="player-info">
              <span class="player-rank">${rankLabel}</span>
              <span class="player-id">${this.escapeHtml(displayName)}${isYou ? ' (You)' : ''}</span>
            </div>
            <div class="player-meta">
              <span class="player-score">${result.total_cracked}</span>
              <span class="player-arrow" aria-hidden="true">${this.playerArrowIcon}</span>
            </div>
          </li>
        `
      })
      .join('')

    listEl.innerHTML = html
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  private escapeAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  private switchTab(tab: LeaderboardTab): void {
    this.currentTab = tab

    // Update tab buttons
    document.querySelectorAll<HTMLButtonElement>('.tab-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tab)
    })

    // Update tab content
    document.querySelectorAll<HTMLElement>('.tab-content').forEach(content => {
      content.classList.toggle('active', content.id === `leaderboard-${tab}`)
    })

    // Load data for the active tab
    if (tab === 'global') {
      this.loadGlobalLeaderboard()
    } else if (tab === 'rivals') {
      this.loadRivals()
    } else {
      this.loadSearchResults()
      this.focusSearchInput()
    }
  }

  async loadLeaderboard(): Promise<void> {
    if (this.currentTab === 'global') {
      await this.loadGlobalLeaderboard()
    } else if (this.currentTab === 'rivals') {
      await this.loadRivals()
    } else {
      await this.loadSearchResults()
    }
  }

  private async loadGlobalLeaderboard(): Promise<void> {
    const listEl = document.getElementById('leaderboardList')
    if (!listEl) return

    listEl.innerHTML = '<li>Loading...</li>'

    try {
      const leaderboard = await apiService.getLeaderboard()
      this.renderGlobalLeaderboard(leaderboard)
    } catch (error) {
      listEl.innerHTML = `<li>Error: ${(error as Error).message}</li>`
    }
  }

  private async loadRivals(): Promise<void> {
    const listEl = document.getElementById('rivalsList')
    const rankEl = document.getElementById('yourRank')
    const totalEl = document.getElementById('totalPlayers')
    
    if (!listEl) return

    listEl.innerHTML = '<li>Loading...</li>'
    if (rankEl) rankEl.textContent = '#-'
    if (totalEl) totalEl.textContent = '-'

    try {
      const rivals = await apiService.getRivals(this.userId, 5)
      this.renderRivals(rivals)
    } catch (error) {
      listEl.innerHTML = `<li>Error: ${(error as Error).message}</li>`
    }
  }

  private renderGlobalLeaderboard(leaderboard: LeaderboardEntry[]): void {
    const listEl = document.getElementById('leaderboardList')
    if (!listEl) return

    if (leaderboard.length === 0) {
      listEl.innerHTML = '<li>No players yet</li>'
      return
    }

    const html = leaderboard
      .map((entry, index) => {
        const rank = index + 1
        const isYou = entry.id === this.userId
        const telegramId = entry.telegram_id ?? entry.id
        const displaySource = entry.display || entry.username || telegramId
        const displayLabel = entry.display
          ? this.shortenPlayerLabel(entry.display)
          : this.formatPlayerId(telegramId)

        return `
          <li${isYou ? ' class="you"' : ''}
            data-player-id="${this.escapeAttribute(entry.id)}"
            data-player-telegram-id="${this.escapeAttribute(telegramId)}"
            data-player-display="${this.escapeAttribute(displaySource)}"
            data-player-username="${this.escapeAttribute(entry.username ?? '')}"
            data-player-rank="${rank}"
            data-player-score="${entry.total_cracked}"
          >
            <div class="player-info">
              <span class="player-rank">#${rank}</span>
              <span class="player-id">${this.escapeHtml(displayLabel)}${isYou ? ' (You)' : ''}</span>
            </div>
            <div class="player-meta">
              <span class="player-score">${entry.total_cracked}</span>
              <span class="player-arrow" aria-hidden="true">${this.playerArrowIcon}</span>
            </div>
          </li>
        `
      })
      .join('')

    listEl.innerHTML = html
  }

  private renderRivals(rivals: RivalsResponse): void {
    const listEl = document.getElementById('rivalsList')
    const rankEl = document.getElementById('yourRank')
    const totalEl = document.getElementById('totalPlayers')
    
    if (!listEl) return

    // Update rank info
    if (rankEl) rankEl.textContent = `#${rivals.rank}`
    if (totalEl) totalEl.textContent = rivals.total.toString()

    // Combine above, you, and below players
    const allPlayers: Array<LeaderboardPlayerSummary & { rank: number; isYou?: boolean }> = []
    
    // Add players above
    rivals.above.forEach((player, index) => {
      allPlayers.push({
        ...player,
        rank: rivals.rank - rivals.above.length + index
      })
    })

    // Add you
    allPlayers.push({
      ...rivals.you,
      rank: rivals.rank,
      isYou: true
    })

    // Add players below
    rivals.below.forEach((player, index) => {
      allPlayers.push({
        ...player,
        rank: rivals.rank + 1 + index
      })
    })

    if (allPlayers.length === 0) {
      listEl.innerHTML = '<li>No rivals found</li>'
      return
    }

    const html = allPlayers
      .map((player) => {
        const telegramId = player.telegram_id ?? player.id
        const username = player.username ?? null
        const displaySource = player.display || username || telegramId
        const displayLabel = player.display
          ? this.shortenPlayerLabel(player.display)
          : this.formatPlayerId(telegramId)

        return `
          <li${player.isYou ? ' class="you"' : ''}
            data-player-id="${this.escapeAttribute(player.id)}"
            data-player-telegram-id="${this.escapeAttribute(telegramId)}"
            data-player-display="${this.escapeAttribute(displaySource)}"
            data-player-username="${this.escapeAttribute(username ?? '')}"
            data-player-rank="${player.rank}"
            data-player-score="${player.total_cracked}"
          >
            <div class="player-info">
              <span class="player-rank">#${player.rank}</span>
              <span class="player-id">${this.escapeHtml(displayLabel)}${player.isYou ? ' (You)' : ''}</span>
            </div>
            <div class="player-meta">
              <span class="player-score">${player.total_cracked}</span>
              <span class="player-arrow" aria-hidden="true">${this.playerArrowIcon}</span>
            </div>
          </li>
        `
      })
      .join('')

    listEl.innerHTML = html
  }

  private formatPlayerId(id: string): string {
    // Show first 6 and last 4 characters of the ID
    if (id.length <= 10) return id
    return `${id.slice(0, 6)}...${id.slice(-4)}`
  }

  private shortenPlayerLabel(label: string): string {
    return label.length <= 10 ? label : `${label.slice(0, 6)}...${label.slice(-4)}`
  }

}